// A perspective-distorted layer keeps its normal x/y/w/h/rotation and adds `props.warp`: how far each corner has moved
// from where the un-distorted layer has it, as fractions of the layer's width and height, measured in the layer's own
// (unrotated) frame. Order: top-left, top-right, bottom-right, bottom-left.

const rectCorners = (w, h) => [ [ -w / 2, -h / 2 ], [ w / 2, -h / 2 ], [ w / 2, h / 2 ], [ -w / 2, h / 2 ] ];

export const hasWarp = layer => !!layer?.props?.warp;

// Corner positions relative to the layer's centre (before rotation), with the distortion applied.
export function getWarpedCorners(w, h, warp) {
	return rectCorners(w, h).map(([ x, y ], i) => warp ? [ x + warp[i][0] * w, y + warp[i][1] * h ] : [ x, y ]);
}

// The inverse of getWarpedCorners: offsets for corner positions given relative to the layer's centre, or null when
// they amount to no distortion.
export function getWarpFromCorners(w, h, corners) {
	const warp = rectCorners(w, h).map(([ x, y ], i) => [ round((corners[i][0] - x) / w), round((corners[i][1] - y) / h) ]);
	return warp.every(([ dx, dy ]) => dx === 0 && dy === 0) ? null : warp;
}

const round = n => Math.round(n * 10000) / 10000 || 0;

// Solves the 3x3 homography mapping four src points onto four dst points. Null when the corners are degenerate.
export function solveHomography(src, dst) {
	const rows = [];
	for(let i = 0; i < 4; i++) {
		const [ x, y ] = src[i];
		const [ u, v ] = dst[i];
		rows.push([ x, y, 1, 0, 0, 0, -u * x, -u * y, u ]);
		rows.push([ 0, 0, 0, x, y, 1, -v * x, -v * y, v ]);
	}
	for(let col = 0; col < 8; col++) {
		let pivot = col;
		for(let r = col + 1; r < 8; r++) if(Math.abs(rows[r][col]) > Math.abs(rows[pivot][col])) pivot = r;
		if(Math.abs(rows[pivot][col]) < 1e-9) return null;
		[ rows[col], rows[pivot] ] = [ rows[pivot], rows[col] ];
		for(let r = 0; r < 8; r++) {
			if(r === col) continue;
			const f = rows[r][col] / rows[col][col];
			for(let c = col; c < 9; c++) rows[r][c] -= f * rows[col][c];
		}
	}
	return rows.map((row, i) => row[8] / row[i]);
}

// CSS transform for a w x h element (in its own pixels) that puts its corners where `warp` says. Meant to follow the
// element's translate/rotate in the transform list, with the default (centre) transform origin. Empty string when the
// layer isn't distorted or the distortion can't be expressed.
export function createWarpTransform(w, h, warp) {
	if(!warp) return '';
	const h8 = solveHomography(rectCorners(w, h), getWarpedCorners(w, h, warp));
	if(!h8) return '';
	const [ a, b, c, d, e, f, g, hh ] = h8;
	return `matrix3d(${[ a, d, 0, g, b, e, 0, hh, 0, 0, 1, 0, c, f, 0, 1 ].join(', ')})`;
}
